import { useCallback, useState } from "react";
import { useStored } from "~/core/hooks";
import type { FilterState } from "~/modules/analytics/components/filters";

/**
 * The filter bar's state.
 *
 * The three segments every screen reads — the window, the engine and the
 * content language — are remembered per browser. The four the insight readings
 * add live for the session only, and two of them belong to one brand: a topic
 * and a citation tag are names that exist in this brand's data and no other's,
 * so a change of brand clears them. The prompt type and the citation category
 * name things every brand has, and survive the switch.
 */
export function useFilters(entityId: string): [FilterState, (next: Partial<FilterState>) => void] {
	const [range, setRange] = useStored<string>("ondit.range", "30d");
	const [engine, setEngine] = useStored<string>("ondit.engine", "");
	const [locale, setLocale] = useStored<string>("ondit.contentLocale", "en");
	const [scoped, setScoped] = useState<Scoped>({ entityId, topics: "", intent: "", category: "", tag: "" });

	// Reset while rendering rather than in an effect: an effect lets one render
	// go out with the old brand's topics against the new brand's numbers.
	const current = scoped.entityId === entityId ? scoped : rescope(scoped, entityId);
	if (current !== scoped) setScoped(current);

	const set = useCallback(
		(next: Partial<FilterState>) => {
			if (next.range !== undefined) setRange(next.range);
			if (next.engine !== undefined) setEngine(next.engine);
			if (next.locale !== undefined) setLocale(next.locale);
			if (
				next.topics !== undefined ||
				next.intent !== undefined ||
				next.category !== undefined ||
				next.tag !== undefined
			) {
				setScoped((was) => {
					const base = was.entityId === entityId ? was : rescope(was, entityId);
					return {
						entityId,
						topics: next.topics ?? base.topics,
						intent: next.intent ?? base.intent,
						category: next.category ?? base.category,
						tag: next.tag ?? base.tag,
					};
				});
			}
		},
		[entityId, setRange, setEngine, setLocale],
	);

	return [
		{
			range,
			engine,
			locale,
			topics: current.topics,
			intent: current.intent,
			category: current.category,
			tag: current.tag,
		},
		set,
	];
}

interface Scoped {
	entityId: string;
	topics: string;
	intent: string;
	category: string;
	tag: string;
}

function rescope(was: Scoped, entityId: string): Scoped {
	return { ...was, entityId, topics: "", tag: "" };
}
